import { apiRequest } from "./api";
import type { Business, BusinessMember } from "../types/index";

export interface CreateBusinessPayload {
  name: string;
  address: string;
}

export interface UpdateBusinessPayload {
  name?: string;
  address?: string;
  status?: "ACTIVE" | "INACTIVE";
  coeCompanyName?: string;
}

export interface InviteMemberPayload {
  mode: "invite";
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  roleId?: string | null;
  isBusinessAdmin: boolean;
}

export interface AssignMemberPayload {
  mode: "assign";
  userId: string;
  roleId?: string | null;
  isBusinessAdmin: boolean;
}

export type AddMemberPayload = InviteMemberPayload | AssignMemberPayload;

export interface UpdateMemberPayload {
  roleId?: string | null;
  isBusinessAdmin?: boolean;
  status?: "ACTIVE" | "INACTIVE";
  excludeFromPayroll?: boolean;
}

export const businessesApi = {
  list: () =>
    apiRequest<{ items: Business[] }>("/admin/businesses"),

  create: (payload: CreateBusinessPayload) =>
    apiRequest<Business>("/admin/businesses", { method: "POST", body: payload }),

  update: (businessId: string, payload: UpdateBusinessPayload) =>
    apiRequest<Business>(`/admin/businesses/${businessId}`, {
      method: "PATCH",
      body: payload,
    }),

  listMembers: (businessId: string) =>
    apiRequest<{ items: BusinessMember[] }>(`/admin/businesses/${businessId}/members`),

  addMember: (businessId: string, payload: AddMemberPayload) =>
    apiRequest<{ profile: unknown; membership: unknown }>(
      `/admin/businesses/${businessId}/members`,
      { method: "POST", body: payload },
    ),

  updateMember: (businessId: string, userId: string, payload: UpdateMemberPayload) =>
    apiRequest<{ success: true }>(`/admin/businesses/${businessId}/members/${userId}`, {
      method: "PATCH",
      body: payload,
    }),

  removeMember: (businessId: string, userId: string) =>
    apiRequest<void>(`/admin/businesses/${businessId}/members/${userId}`, {
      method: "DELETE",
    }),

  // Number of ACTIVE businesses the user belongs to (used to warn before removing
  // them from their only business).
  memberMembershipCount: (businessId: string, userId: string) =>
    apiRequest<{ count: number }>(
      `/admin/businesses/${businessId}/members/${userId}/membership-count`,
    ),

  uploadLogo: (businessId: string, imageData: string, contentType: string) =>
    apiRequest<{ logoUrl: string; signedUrl: string }>(
      `/admin/businesses/${businessId}/logo`,
      { method: "POST", body: { imageData, contentType } },
    ),
};
